import { DateTime } from "luxon";
import type { Department } from "../../../../../contexts/AuthProvider";
import { backendService } from "../../../../../services/backend.service";
import { getStaffingDate } from "../../../../../utils/dateUtils";
import type { HrisIntegrationType } from "../../../../../utils/models";

export type SummaryDateRange = { start: DateTime; end: DateTime };

/** What a default range needs from the department: its clock and where its schedule day starts. */
export type SummaryRangeDepartment = Pick<Department, "timezone" | "dayStart">;

export type SummaryDialogConfig = {
	titleKey: string;
	/** Kebab-case name used in every `data-testid` and in the XLSX file name. */
	slug: string;
	loadErrorTitleKey: string;
	/** Heading over the date picker — says which days the range selects. */
	dateFilterTitleKey: string;
	/** The backend's catch-all load error. Any other key it sends is a specific reason worth showing. */
	loadFailedKey: string;
	/** The mapping tab fixes role strings and time off types; shifts have nothing to map there. */
	showMappingBanner: boolean;
	getDefaultRange: (department: SummaryRangeDepartment) => SummaryDateRange;
	download: typeof backendService.downloadIntegrationEmployeesSummary;
};

const SHIFTS_DEFAULT_WINDOW_DAYS = 30;

// DEFAULT: Employees and time off open on yesterday through today so support sees the latest sync.
const latestSyncRange = ({ timezone }: SummaryRangeDepartment): SummaryDateRange => {
	const today = DateTime.now().setZone(timezone).startOf("day");
	return { start: today.minus({ days: 1 }), end: today };
};

// DEFAULT: the last 30 schedule days ending YESTERDAY. Today's punches are still arriving, so including
// today would count shifts that simply haven't been punched yet as "No punches". "Yesterday" is a
// schedule day, not a calendar date: between midnight and the department's day start the schedule day
// in progress is still the previous calendar date, and the backend buckets shifts the same way.
const lastThirtyDaysEndingYesterday = ({ timezone, dayStart }: SummaryRangeDepartment): SummaryDateRange => {
	const yesterday = getStaffingDate(DateTime.now().setZone(timezone), dayStart).startOf("day").minus({ days: 1 });
	return { start: yesterday.minus({ days: SHIFTS_DEFAULT_WINDOW_DAYS - 1 }), end: yesterday };
};

export const SUMMARY_DIALOG_CONFIG: Record<HrisIntegrationType, SummaryDialogConfig> = {
	employees: {
		titleKey: "integration.summary.employees.title",
		slug: "employees",
		loadErrorTitleKey: "integration.summary.employees.loadError.title",
		dateFilterTitleKey: "integration.summary.dateFilter.title",
		loadFailedKey: "integration.summary.employees.load.failed",
		showMappingBanner: true,
		getDefaultRange: latestSyncRange,
		download: backendService.downloadIntegrationEmployeesSummary,
	},
	time_off: {
		titleKey: "integration.summary.timeoff.title",
		slug: "time-off",
		loadErrorTitleKey: "integration.summary.timeoff.loadError.title",
		dateFilterTitleKey: "integration.summary.dateFilter.title",
		loadFailedKey: "integration.summary.timeoff.load.failed",
		showMappingBanner: true,
		getDefaultRange: latestSyncRange,
		download: backendService.downloadIntegrationTimeOffSummary,
	},
	shifts: {
		titleKey: "integration.summary.shifts.title",
		slug: "shifts",
		loadErrorTitleKey: "integration.summary.shifts.loadError.title",
		dateFilterTitleKey: "integration.summary.shifts.dateFilter.title",
		loadFailedKey: "integration.summary.shifts.load.failed",
		showMappingBanner: false,
		getDefaultRange: lastThirtyDaysEndingYesterday,
		download: backendService.downloadIntegrationShiftsSummary,
	},
};
